<template>
  <section>
    <VCard>
      <VCardText>
        <div class="d-flex align-center justify-space-between flex-wrap mb-4 gap-4">
          <h4 class="text-h4">
            {{ t('shipment.viewPackingList') }}
            <span
              v-if="packingList"
              class="text-primary"
            >
              #{{ packingListId }}
            </span>
          </h4>
          
          <div class="d-flex gap-2">
            <VBtn
              color="secondary"
              variant="outlined"
              :to="{ name: 'shipment-packing-lists' }"
              prepend-icon="tabler-arrow-left"
            >
              {{ t('actions.back') }}
            </VBtn>
            
            <VBtn
              color="success"
              variant="outlined"
              prepend-icon="tabler-file-export"
              @click="exportToExcel"
            >
              {{ t('actions.export') }}
            </VBtn>
            
            <VBtn
              v-if="packingList"
              color="warning"
              :to="`/shipment/packing-lists/${packingListId}/edit`"
              prepend-icon="tabler-edit"
            >
              {{ t('actions.edit') }}
            </VBtn>
            
            <VBtn
              v-if="packingList"
              color="info"
              prepend-icon="tabler-printer"
              @click="printPackingList"
            >
              {{ t('actions.print') }}
            </VBtn>
          </div>
        </div>
      </VCardText>
      
      <!-- Loading State -->
      <VCardText v-if="loading">
        <div class="d-flex justify-center py-8">
          <VProgressCircular
            indeterminate
            color="primary"
            size="48"
          />
        </div>
      </VCardText>
      
      <!-- Not Found State -->
      <VCardText v-else-if="!packingList">
        <VAlert
          type="error"
          :title="t('messages.notFound')"
          :text="t('messages.packingListNotFoundOrNoPermission')"
        />
      </VCardText>
      
      <!-- Content when loaded -->
      <div v-else>
        <!-- Packing List Details -->
        <VCardText>
          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <VCard
                :title="t('shipment.packingListDetails')"
                :subtitle="t('shipment.basicInfoAboutPackingList')"
                class="mb-4"
              >
                <VCardText>
                  <div class="d-flex flex-column gap-2">
                    <div class="d-flex justify-space-between">
                      <span class="text-secondary">{{ t('tableHeaders.id') }}:</span>
                      <span class="font-weight-medium">{{ packingList.id }}</span>
                    </div>
                    
                    <div class="d-flex justify-space-between">
                      <span class="text-secondary">{{ t('tableHeaders.date') }}:</span>
                      <span class="font-weight-medium">{{ formatDate(packingList.created_at) }}</span>
                    </div>
                    
                    <div class="d-flex justify-space-between">
                      <span class="text-secondary">{{ t('shipment.referenceNumber') }}:</span>
                      <span class="font-weight-medium">{{ packingList.reference || '-' }}</span>
                    </div>
                    
                    <div class="d-flex justify-space-between">
                      <span class="text-secondary">{{ t('shipment.transportMethod') }}:</span>
                      <VChip
                        :color="packingList.transport === 0 ? 'primary' : 'secondary'"
                        size="small"
                      >
                        {{ packingList.transport === 0 ? t('shipment.by_plane') : t('shipment.by_ship') }}
                      </VChip>
                    </div>
                    
                    <div class="d-flex justify-space-between">
                      <span class="text-secondary">{{ t('tableHeaders.status') }}:</span>
                      <VChip
                        :color="getStatusColor(packingList.status)"
                        size="small"
                      >
                        {{ t(`shipment.statuses.${packingList.status}`, packingList.status) }}
                      </VChip>
                    </div>
                    
                    <div class="d-flex justify-space-between">
                      <span class="text-secondary">{{ t('shipment.additionalInfo1') }}:</span>
                      <span class="font-weight-medium">{{ packingList.other_field1 || '-' }}</span>
                    </div>
                    
                    <div class="d-flex justify-space-between">
                      <span class="text-secondary">{{ t('shipment.additionalInfo2') }}:</span>
                      <span class="font-weight-medium">{{ packingList.other_field2 || '-' }}</span>
                    </div>
                  </div>
                </VCardText>
              </VCard>
            </VCol>
            
            <VCol
              cols="12"
              md="6"
            >
              <VCard
                v-if="packingList.container_arrival"
                :title="t('shipment.containerDetails')"
                :subtitle="t('shipment.infoAboutContainer')"
                class="mb-4"
              >
                <VCardText>
                  <div class="d-flex flex-column gap-2">
                    <div class="d-flex justify-space-between">
                      <span class="text-secondary">{{ t('tableHeaders.id') }}:</span>
                      <span class="font-weight-medium">#{{ packingList.container_arrival.id }}</span>
                    </div>
                    
                    <div
                      v-if="isSuperadmin"
                      class="d-flex justify-space-between"
                    >
                      <span class="text-secondary">{{ t('shipment.container_number') }}:</span>
                      <span class="font-weight-medium">{{ packingList.container_arrival.container_number || '-' }}</span>
                    </div>
                    <div
                      v-else
                      class="d-flex justify-space-between"
                    >
                      <span class="text-secondary">{{ t('shipment.reference_code') || 'Reference' }}:</span>
                      <span class="font-weight-medium">{{ packingList.container_arrival.reference_code || packingList.reference_code || '-' }}</span>
                    </div>
                    
                    <div class="d-flex justify-space-between">
                      <span class="text-secondary">{{ t('shipment.arrival_date') }}:</span>
                      <span class="font-weight-medium">
                        {{ packingList.container_arrival.arrival_date ? formatDate(packingList.container_arrival.arrival_date) : '-' }}
                      </span>
                    </div>
                    
                    <div class="d-flex justify-space-between">
                      <span class="text-secondary">{{ t('shipment.departure_date') }}:</span>
                      <span class="font-weight-medium">
                        {{ packingList.container_arrival.departure_date ? formatDate(packingList.container_arrival.departure_date) : '-' }}
                      </span>
                    </div>
                    
                    <div class="d-flex justify-space-between">
                      <span class="text-secondary">{{ t('shipment.vessel_name') }}:</span>
                      <span class="font-weight-medium">{{ packingList.container_arrival.vessel_name || '-' }}</span>
                    </div>
                    
                    <div class="d-flex justify-space-between">
                      <span class="text-secondary">{{ t('shipment.voyage_number') }}:</span>
                      <span class="font-weight-medium">{{ packingList.container_arrival.voyage_number || '-' }}</span>
                    </div>
                    
                    <div class="d-flex justify-space-between">
                      <span class="text-secondary">{{ t('tableHeaders.status') }}:</span>
                      <VChip
                        :color="getContainerStatusColor(packingList.container_arrival.status)"
                        size="small"
                      >
                        {{ t(`shipment.containerStatuses.${packingList.container_arrival.status}`, packingList.container_arrival.status) }}
                      </VChip>
                    </div>
                  </div>
                </VCardText>
              </VCard>
              
              <VCard
                v-else
                :title="t('shipment.containerDetails')"
                :subtitle="t('shipment.noContainerAssigned')"
                class="mb-4"
              >
                <VCardText>
                  <VAlert
                    type="info"
                    density="compact"
                  >
                    {{ t('shipment.noContainerAssigned') }}
                  </VAlert>
                </VCardText>
              </VCard>
            </VCol>
          </VRow>
          
          <!-- Summary Card -->
          <VCard
            :title="t('shipment.packingListSummary')"
            :subtitle="t('shipment.overviewOfPackages')"
            class="mb-4"
          >
            <VCardText>
              <VRow class="text-center">
                <VCol
                  cols="12"
                  sm="4"
                >
                  <div class="text-caption text-secondary mb-1">
                    {{ t('shipment.totalPackages') }}
                  </div>
                  <h4 class="text-h4">
                    {{ packingList.total_package || 0 }}
                  </h4>
                </VCol>
                
                <VCol
                  cols="12"
                  sm="4"
                >
                  <div class="text-caption text-secondary mb-1">
                    {{ t('shipment.totalWeight') }}
                  </div>
                  <h4 class="text-h4">
                    {{ packingList.weight ? `${packingList.weight.toFixed(2)} kg` : '0.00 kg' }}
                  </h4>
                </VCol>
                
                <VCol
                  cols="12"
                  sm="4"
                >
                  <div class="text-caption text-secondary mb-1">
                    {{ t('shipment.totalVolume') }}
                  </div>
                  <h4 class="text-h4">
                    {{ packingList.volume ? `${packingList.volume.toFixed(2)} m³` : '0.00 m³' }}
                  </h4>
                </VCol>
              </VRow>
            </VCardText>
          </VCard>
          
          <!-- Package List with Pagination -->
          <VCard>
            <VTabs v-model="activeTab">
              <VTab value="packages">
                {{ t('shipment.outgoing_packages') }}
                <VBadge
                  v-if="packagesTotal > 0"
                  :content="packagesTotal"
                  color="primary"
                  class="ms-2 bdg-count-list"
                />
              </VTab>
              <VTab value="parcels">
                {{ t('shipment.incoming_parcels') }}
                <VBadge
                  v-if="parcelsTotal > 0"
                  :content="parcelsTotal"
                  color="primary"
                  class="ms-2 bdg-count-list"
                />
              </VTab>
            </VTabs>
            
            <VDivider />
            
            <VWindow v-model="activeTab">
              <!-- Packages Tab -->
              <VWindowItem value="packages">
                <div class="pa-4">
                  <!-- Search and Filters -->
                  <VRow class="mb-4">
                    <VCol
                      cols="12"
                      md="4"
                    >
                      <VTextField
                        v-model="packagesSearch"
                        :label="t('common.search')"
                        prepend-inner-icon="tabler-search"
                        density="comfortable"
                        clearable
                        @update:model-value="onPackagesSearchChange"
                      />
                    </VCol>
                    <VCol
                      cols="12"
                      md="4"
                    >
                      <VSelect
                        v-model="packagesStatusFilter"
                        :items="statusFilterOptions"
                        :label="t('tableHeaders.status')"
                        density="comfortable"
                        clearable
                        @update:model-value="onPackagesFilterChange"
                      />
                    </VCol>
                  </VRow>
                  
                  <!-- Table -->
                  <VDataTableServer
                    v-if="paginatedPackages && paginatedPackages.length > 0"
                    :headers="packagesHeaders"
                    :items="paginatedPackages"
                    :loading="packagesLoading"
                    :items-length="packagesTotal"
                    :items-per-page="packagesPerPage"
                    item-value="id"
                    @update:options="onPackagesOptionsUpdate">
                    <template #item.id="{ item }">
                      {{ item.outgoing_package.id }}
                    </template>
                    <template #item.barcode="{ item }">
                      {{ item.outgoing_package.barcode }}
                    </template>
                    <template #item.marking="{ item }">
                      {{ item.outgoing_package.marking || '-' }}
                    </template>
                    <template #item.products="{ item }">
                      {{ getLocalizedProductsDisplayText(item.outgoing_package) }}
                    </template>
                    <template #item.weight="{ item }">
                      {{ item.outgoing_package.weight ? `${item.outgoing_package.weight} kg` : '-' }}
                    </template>
                    <template #item.volume="{ item }">
                      {{ item.outgoing_package.volume ? `${item.outgoing_package.volume} m³` : '-' }}
                    </template>
                    <template #item.dimension="{ item }">
                      {{ item.outgoing_package.dimension || '-' }}
                    </template>
                    <template #item.parcels="{ item }">
                      <VChip
                        v-if="item.outgoing_package.incoming_parcels && item.outgoing_package.incoming_parcels.length > 0"
                        color="primary"
                        size="small"
                      >
                        {{ item.outgoing_package.incoming_parcels.length }}
                      </VChip>
                      <span v-else>-</span>
                    </template>
                    <template #item.status="{ item }">
                      <VChip
                        :color="item.outgoing_package.status_color || getStatusColor(item.outgoing_package.status)"
                        size="small"
                      >
                        <VIcon
                          v-if="item.outgoing_package.status_icon"
                          :icon="item.outgoing_package.status_icon"
                          size="x-small"
                          class="me-1"
                        />
                        {{ t(`shipment.statuses.${item.outgoing_package.status}`, item.outgoing_package.status) }}
                      </VChip>
                    </template>
                  </VDataTableServer>
                  
                  <div v-else class="text-center py-4">
                    {{ t('messages.noData') }}
                  </div>
                </div>
              </VWindowItem>
              
              <!-- Parcels Tab -->
              <VWindowItem value="parcels">
                <div class="pa-4">
                  <!-- Search and Filters -->
                  <VRow class="mb-4">
                    <VCol
                      cols="12"
                      md="4"
                    >
                      <VTextField
                        v-model="parcelsSearch"
                        :label="t('common.search')"
                        prepend-inner-icon="tabler-search"
                        density="comfortable"
                        clearable
                        @update:model-value="onParcelsSearchChange"
                      />
                    </VCol>
                    <VCol
                      cols="12"
                      md="4"
                    >
                      <VSelect
                        v-model="parcelsStatusFilter"
                        :items="statusFilterOptions"
                        :label="t('tableHeaders.status')"
                        density="comfortable"
                        clearable
                        @update:model-value="onParcelsFilterChange"
                      />
                    </VCol>
                  </VRow>
                  
                  <!-- Table -->
                  <VDataTableServer
                    v-if="paginatedParcels && paginatedParcels.length > 0"
                    :headers="parcelsHeaders"
                    :items="paginatedParcels"
                    :loading="parcelsLoading"
                    :items-length="parcelsTotal"
                    :items-per-page="parcelsPerPage"
                    item-value="id"
                    @update:options="onParcelsOptionsUpdate">
                    <template #item.id="{ item }">
                      {{ item.id }}
                    </template>
                    <template #item.barcode="{ item }">
                      {{ item.barcode }}
                    </template>
                    <template #item.bag_code="{ item }">
                      {{ item.bag_code || '-' }}
                    </template>
                    <template #item.customer="{ item }">
                      <CustomerLink :customer="item.customer" :show-client-id="false" />
                    </template>
                    <template #item.team="{ item }">
                      <TeamBadge
                        :team="item.team"
                        :code="item.team_code"
                        size="small"
                      />
                    </template>
                    <template #item.package="{ item }">
                      <template v-if="item.outgoing_packages && item.outgoing_packages[0]">
                        {{ item.outgoing_packages[0].barcode }}
                      </template>
                      <span v-else>-</span>
                    </template>
                    <template #item.products="{ item }">
                      {{ getLocalizedProductsDisplayText(item) }}
                    </template>
                    <template #item.weight="{ item }">
                      {{ item.weight ? `${item.weight} kg` : '-' }}
                    </template>
                    <template #item.volume="{ item }">
                      {{ item.volume ? `${item.volume} m³` : '-' }}
                    </template>
                    <template #item.status="{ item }">
                      <VChip
                        :color="item.status_color || getStatusColor(item.status)"
                        size="small"
                      >
                        <VIcon
                          v-if="item.status_icon"
                          :icon="item.status_icon"
                          size="x-small"
                          class="me-1"
                        />
                        {{ t(`shipment.statuses.${item.status_key || item.status}`, item.status) }}
                      </VChip>
                    </template>
                  </VDataTableServer>
                  
                  <div v-else class="text-center py-4">
                    {{ t('messages.noData') }}
                  </div>
                </div>
              </VWindowItem>
            </VWindow>
          </VCard>
        </VCardText>
      </div>
    </VCard>
  </section>
</template>

<script setup>
definePage({
  meta: {
    requiresAuth: true,
    action: 'read',
    subject: 'Packinglist',
  },
})

import { ref, onMounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { usePackingListsManagement } from '@/composables/usePackingListsManagement'
import { useSnackbar } from '@/composables/useSnackbar'
import { useSuperadmin } from '@/composables/useSuperadmin'
import { getStatusColor } from '@/utils/statusUtils'
import axios from 'axios'

const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()
const { error } = useSnackbar()
const { fetchPackingListWithPagination } = usePackingListsManagement()
const { isSuperadmin } = useSuperadmin()

// Function to map frontend locale to backend locale
const mapLocale = frontendLocale => {
  const localeMap = {
    'zh': 'ce',
    'cn': 'ce',
    'fr': 'fr',
    'en': 'en',
  }
  
  return localeMap[frontendLocale] || frontendLocale
}

// Function to get localized product name from a translations object
const getLocalizedProductName = product => {
  if (!product) return '-'
  
  if (typeof product.name === 'string') return product.name
  
  if (product.name && typeof product.name === 'object') {
    const backendLocale = mapLocale(locale.value)
    
    if (product.name[backendLocale]) {
      return product.name[backendLocale]
    }
    
    if (product.name.en) {
      return product.name.en
    }
    
    if (product.name.ce) {
      return product.name.ce
    }
    
    const firstKey = Object.keys(product.name)[0]
    if (firstKey) {
      return product.name[firstKey]
    }
  }
  
  return '-'
}

// Get localized products display text
const getLocalizedProductsDisplayText = item => {
  if (!item || 
      (!item.products && !item.products_display_details) || 
      (item.products && item.products.length === 0 && (!item.products_display_details || item.products_display_details.length === 0))) {
    return '-'
  }
  
  if (item.products && item.products.length > 0) {
    return item.products.map(product => {
      const name = getLocalizedProductName(product)
      
      return `${name} (x${product.quantity})`
    }).join(', ')
  }
  
  if (item.products_display_details && item.products_display_details.length > 0) {
    return item.products_display_details.map(product => {
      const name = typeof product.name === 'object' 
        ? getLocalizedProductName(product) 
        : product.name

      
      return `${name} (x${product.quantity})`
    }).join(', ')
  }
  
  return item.products_display_text || '-'
}

// Page state
const packingListId = route.params.id
const packingList = ref(null)
const loading = ref(true)
const activeTab = ref('packages')

const packagesLoading = ref(false)
const parcelsLoading = ref(false)

// Pagination state for packages
const packagesPage = ref(1)
const packagesPerPage = ref(10)
const packagesMeta = ref(null)
const paginatedPackages = ref([])
const packagesSearch = ref('')
const packagesStatusFilter = ref(null)

// Pagination state for parcels
const parcelsPage = ref(1)
const parcelsPerPage = ref(10)
const parcelsMeta = ref(null)
const paginatedParcels = ref([])
const parcelsSearch = ref('')
const parcelsStatusFilter = ref(null)

// Computed properties
const packagesTotal = computed(() => packagesMeta.value?.total || 0)
const packagesTotalPages = computed(() => packagesMeta.value?.last_page || 1)

const parcelsTotal = computed(() => parcelsMeta.value?.total || 0)
const parcelsTotalPages = computed(() => parcelsMeta.value?.last_page || 1)

// Status filter options
const statusFilterOptions = computed(() => [
  { title: t('shipment.statuses.pending'), value: 'pending' },
  { title: t('shipment.statuses.packaged'), value: 'packaged' },
  { title: t('shipment.statuses.packed'), value: 'packed' },
  { title: t('shipment.statuses.loaded'), value: 'loaded' },
  { title: t('shipment.statuses.shipped'), value: 'shipped' },
  { title: t('shipment.statuses.completed'), value: 'completed' },
  { title: t('shipment.statuses.received'), value: 'received' },
])

// Table headers for packages
const packagesHeaders = computed(() => [
  { title: t('tableHeaders.id'), key: 'id', sortable: false },
  { title: t('tableHeaders.barcode'), key: 'barcode', sortable: false },
  { title: t('tableHeaders.marking'), key: 'marking', sortable: false },
  { title: t('tableHeaders.products'), key: 'products', sortable: false },
  { title: t('tableHeaders.weight'), key: 'weight', sortable: false },
  { title: t('tableHeaders.volume'), key: 'volume', sortable: false },
  { title: t('tableHeaders.dimension'), key: 'dimension', sortable: false },
  { title: t('tableHeaders.parcels'), key: 'parcels', sortable: false },
  { title: t('tableHeaders.status'), key: 'status', sortable: false },
])

// Table headers for parcels
const parcelsHeaders = computed(() => [
  { title: t('tableHeaders.id'), key: 'id', sortable: false },
  { title: t('tableHeaders.barcode'), key: 'barcode', sortable: false },
  { title: t('tableHeaders.bagCode'), key: 'bag_code', sortable: false },
  { title: t('tableHeaders.customer'), key: 'customer', sortable: false },
  // Team column drives the per-team breakdown in the report. Useful here
  // to spot Sheet1-only imports (only GD/JP loaded, GB sheet missed) at
  // a glance without leaving the PL view.
  { title: t('tableHeaders.team'), key: 'team', sortable: false },
  { title: t('tableHeaders.package'), key: 'package', sortable: false },
  { title: t('tableHeaders.products'), key: 'products', sortable: false },
  { title: t('tableHeaders.weight'), key: 'weight', sortable: false },
  { title: t('tableHeaders.volume'), key: 'volume', sortable: false },
  { title: t('tableHeaders.status'), key: 'status', sortable: false },
])

// Helper function to format date
const formatDate = dateString => {
  if (!dateString) return '-'
  
  try {
    const date = new Date(dateString)
    const currentLocale = locale.value || 'en'
    
    return new Intl.DateTimeFormat(currentLocale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date)
  } catch (e) {
    return dateString
  }
}


// Helper function to get container status color
const getContainerStatusColor = status => {
  switch (status) {
  case 'empty':
    return 'primary'
  case 'loaded':
    return 'info'
  case 'shipped':
    return 'warning'
  case 'in_port':
    return 'success'
  case 'unloaded':
    return 'grey'
  default:
    return 'grey'
  }
}

// Fetch packing list data with pagination
const fetchData = async (isPagination = false) => {
  // Si c'est une pagination, on active seulement le loading de la liste concernée
  if (isPagination) {
    if (activeTab.value === 'packages') {
      packagesLoading.value = true
    } else {
      parcelsLoading.value = true
    }
  } else {
    // Pour le chargement initial
    loading.value = true
  }
  
  try {
    const params = {
      include_translations: true,
      packages_page: packagesPage.value,
      packages_per_page: packagesPerPage.value,
      packages_search: packagesSearch.value || undefined,
      packages_status: packagesStatusFilter.value || undefined,
      parcels_page: parcelsPage.value,
      parcels_per_page: parcelsPerPage.value,
      parcels_search: parcelsSearch.value || undefined,
      parcels_status: parcelsStatusFilter.value || undefined,
    }
    
    const data = await fetchPackingListWithPagination(packingListId, params)
    
    if (data) {
      packingList.value = data
      packagesMeta.value = data.packages_meta
      parcelsMeta.value = data.parcels_meta
      paginatedPackages.value = data.paginated_packages
      paginatedParcels.value = data.paginated_parcels
    } else {
      error(t('messages.packingListNotFound', 'Packing list not found'))
    }
  } catch (err) {
    console.error('Error fetching packing list:', err)
    error(t('messages.fetchError', 'Error fetching packing list details'))
  } finally {
    loading.value = false
    packagesLoading.value = false
    parcelsLoading.value = false
  }
}

// Pagination and filter handlers for packages
const onPackagesOptionsUpdate = options => {
  if (options.page !== packagesPage.value) {
    packagesPage.value = options.page
  }
  if (options.itemsPerPage !== packagesPerPage.value) {
    packagesPerPage.value = options.itemsPerPage
    packagesPage.value = 1
  }
  fetchData(true)
}

const onPackagesSearchChange = () => {
  packagesPage.value = 1
  fetchData(true)
}

const onPackagesFilterChange = () => {
  packagesPage.value = 1
  fetchData(true)
}

// Pagination and filter handlers for parcels
const onParcelsOptionsUpdate = options => {
  if (options.page !== parcelsPage.value) {
    parcelsPage.value = options.page
  }
  if (options.itemsPerPage !== parcelsPerPage.value) {
    parcelsPerPage.value = options.itemsPerPage
    parcelsPage.value = 1
  }
  fetchData(true)
}

const onParcelsSearchChange = () => {
  parcelsPage.value = 1
  fetchData(true)
}

const onParcelsFilterChange = () => {
  parcelsPage.value = 1
  fetchData(true)
}

// Print packing list
const printPackingList = async () => {
  try {
    const response = await axios.get(`/shipment/packing-lists/${packingListId}/print`, {
      responseType: 'text',
    })

    const printWindow = window.open('', '_blank')
    if (!printWindow) return

    printWindow.document.open()
    printWindow.document.write(response.data)
    printWindow.document.close()
  } catch (err) {
    console.error('Error printing packing list:', err)
    error(t('messages.printError', 'Error printing packing list'))
  }
}

// Export to Excel function
const exportToExcel = async () => {
  try {
    const currentLocale = locale.value
    
    const response = await axios.get(`/shipment/packing-lists/${packingListId}/export`, {
      params: { 
        locale: currentLocale,
        include_translations: true,
      },
      responseType: 'blob',
    })
    
    const blob = new Blob([response.data], { type: response.headers['content-type'] })
    const url = window.URL.createObjectURL(blob)
    
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `packing-list-${packingListId}.xlsx`)
    document.body.appendChild(link)
    link.click()
    
    window.URL.revokeObjectURL(url)
    document.body.removeChild(link)
  } catch (err) {
    console.error('Error exporting to Excel:', err)
    error(t('messages.exportError', 'Error exporting packing list to Excel'))
  }
}

// Lifecycle hooks
onMounted(() => {
  fetchData()
})
</script>

<style scoped>
.text-secondary {
  opacity: 0.7;
}
.bdg-count-list {
  padding-left: 10px;
}
</style>
